import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Button, Typography } from '@material-tailwind/react';
import { FaTimes } from 'react-icons/fa';
import { Loader2, Minus, Plus, Users } from 'lucide-react';
import { UltraBillingInterval } from '../../utils/helpers/ultraCheckout';

const MIN_SEATS = 1;
const MAX_SEATS = 100;

type Props = {
  open: boolean;
  interval: UltraBillingInterval | null;
  planLabel: string;
  unitPrice: number;
  cadence: string;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (quantity: number) => void;
};

function formatUsd(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return rounded % 1 === 0 ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}

export default function UltraSeatsModal({
  open,
  interval,
  planLabel,
  unitPrice,
  cadence,
  isSubmitting,
  onClose,
  onConfirm,
}: Props) {
  const [seats, setSeats] = useState(MIN_SEATS);

  useEffect(() => {
    if (open) setSeats(MIN_SEATS);
  }, [open]);

  const setClampedSeats = (value: number) => {
    if (Number.isNaN(value)) return;
    setSeats(Math.min(MAX_SEATS, Math.max(MIN_SEATS, Math.floor(value))));
  };

  const total = unitPrice * seats;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-[100] inset-0 overflow-y-auto"
        onClose={() => {
          if (!isSubmitting) onClose();
        }}
      >
        <div className="flex items-center justify-center min-h-screen p-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative bg-white rounded-3xl text-left shadow-2xl transform transition-all sm:my-8 sm:max-w-md sm:w-full border border-gray-100 overflow-hidden">
              <div className="absolute top-3 right-3 z-10">
                <button
                  type="button"
                  className="bg-white/80 backdrop-blur-sm rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-all focus:outline-none"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              </div>

              <div className="px-6 pt-8 pb-6">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-teal-600" />
                </div>
                <Typography
                  variant="h5"
                  className="text-gray-900 font-bold mb-1 leading-tight"
                >
                  How many seats?
                </Typography>
                <Typography className="text-confirmed-secondary font-bold text-[10px] uppercase tracking-wider mb-4">
                  {planLabel} plan · {formatUsd(unitPrice)} {cadence}
                </Typography>
                <Typography className="text-gray-600 text-sm mb-6">
                  Your team will be created with this many seats after payment.
                  One seat is enough if you are buying for yourself.
                </Typography>

                <div className="flex items-center justify-center gap-4 mb-6">
                  <button
                    type="button"
                    className="w-11 h-11 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    onClick={() => setClampedSeats(seats - 1)}
                    disabled={seats <= MIN_SEATS || isSubmitting}
                    aria-label="Decrease seats"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    min={MIN_SEATS}
                    max={MAX_SEATS}
                    value={seats}
                    onChange={(e) => setClampedSeats(Number(e.target.value))}
                    className="w-20 text-center text-3xl font-black text-gray-900 border-0 border-b-2 border-confirmed-secondary focus:ring-0 bg-transparent"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="w-11 h-11 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    onClick={() => setClampedSeats(seats + 1)}
                    disabled={seats >= MAX_SEATS || isSubmitting}
                    aria-label="Increase seats"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-gray-50/80 rounded-2xl p-4 mb-6 border border-gray-100 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    {seats} {seats === 1 ? 'seat' : 'seats'}
                  </span>
                  <span className="text-lg font-black text-gray-900">
                    {formatUsd(total)}
                    <span className="text-sm font-medium text-gray-500">
                      {interval === 'yearly' ? ' / year' : ' / month'}
                    </span>
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    size="md"
                    className="bg-confirmed-secondary hover:bg-secondary-hover text-white py-3.5 rounded-xl shadow-md hover:shadow-confirmed-secondary/20 transition-all font-bold text-sm flex items-center justify-center gap-2"
                    onClick={() => onConfirm(seats)}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Opening checkout…
                      </>
                    ) : (
                      'Continue to checkout'
                    )}
                  </Button>
                  <button
                    type="button"
                    className="text-gray-500 hover:text-gray-700 font-bold text-xs py-1 transition-colors"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Back to plans
                  </button>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
